import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { theme } from 'styles/theme/hav-theme'

import type { BadgeAppearance, BadgeColor, BadgeSize } from './Badge'

interface ContainerProps {
  appearance: BadgeAppearance
  color: BadgeColor
  size: BadgeSize
}

const getColors = (appearance: BadgeAppearance, colorKey: BadgeColor) => {
  const ionColors = theme.colors.ion
  const colorPalette = ionColors[colorKey]

  switch (appearance) {
    case 'solid':
      return css`
        background-color: ${colorPalette.main};
        color: ${colorPalette.onmain};
      `
    case 'soft':
      return css`
        background-color: ${colorPalette.softer};
        color: ${colorPalette.onsofter};
      `
    case 'outline':
      return css`
        background-color: transparent;
        color: ${colorPalette.strong};
        border: 1px solid ${colorPalette.strong};
      `
    case 'surface':
      return css`
        background-color: ${ionColors.surface.default};
        color: ${colorPalette.strong};
        border: 1px solid ${ionColors.state.enabled.borderneutral};
      `
    case 'ping':
      return css`
        background-color: ${colorPalette.main};
        color: transparent;
        border: none;
        padding: 0;
        min-width: 0;
      `
    default:
      return css``
  }
}

const getSizes = (size: BadgeSize, appearance: BadgeAppearance) => {
  if (appearance === 'ping') {
    return css`
      width: 8px;
      height: 8px;
      border-radius: 50%;
    `
  }

  switch (size) {
    case 'small':
      return css`
        height: 20px;
        padding: 0 8px;
        font-size: ${theme.typography.sizes['2xs']};
        gap: 4px;
        border-radius: 9999px;
      `
    case 'medium':
    default:
      return css`
        height: 28px;
        padding: 0 8px;
        font-size: ${theme.typography.sizes.xs};
        gap: 4px;
        border-radius: 9999px;
      `
  }
}

export const Container = styled.div<ContainerProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.typography.family.primary};
  font-weight: ${theme.typography.weight.sm};
  white-space: nowrap;
  transition: all 0.2s ease-in-out;

  ${({ appearance, color }) => getColors(appearance, color)}
  ${({ size, appearance }) => getSizes(size, appearance)}
`

export const Label = styled.span`
  line-height: ${theme.typography.lineHeight.sm};
`
